import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { importAllocated } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import type { AllocationImportResponse } from "@/api/types";

export default function AirLogiImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AllocationImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mut = useMutation({
    mutationFn: (f: File) => importAllocated(f),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
      setResult(null);
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-brand-primary">出荷実績取込</h2>
        <p className="text-sm text-gray-500 mt-1">
          エアロジからダウンロードした出荷実績 CSV をアップロードして、引き当て情報を Wonect Picking に取り込みます。
        </p>
      </div>

      <div className="card space-y-4">
        <div>
          <label className="label">出荷実績 CSV (cp932)</label>
          <input
            type="file"
            accept=".csv,text/csv"
            className="input"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <button
          className="btn-primary"
          disabled={!file || mut.isPending}
          onClick={() => file && mut.mutate(file)}
        >
          {mut.isPending ? "取り込み中…" : "アップロードして取り込み"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          {error}
        </div>
      )}

      {result && (
        <div className="card space-y-4">
          <h3 className="text-lg font-semibold">取込結果</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="stat-card">
              <span className="text-sm text-gray-500">読み込み行</span>
              <span className="text-2xl font-bold">{result.rows_read}</span>
            </div>
            <div className="stat-card">
              <span className="text-sm text-gray-500">更新済み注文</span>
              <span className="text-2xl font-bold text-brand-accent">
                {result.orders_updated}
              </span>
            </div>
            <div className="stat-card">
              <span className="text-sm text-gray-500">更新済み明細</span>
              <span className="text-2xl font-bold text-brand-accent">
                {result.items_updated}
              </span>
            </div>
            <div className="stat-card">
              <span className="text-sm text-gray-500">未マッチ注文</span>
              <span
                className={`text-2xl font-bold ${
                  result.unmatched_orders.length ? "text-brand-alert" : "text-gray-400"
                }`}
              >
                {result.unmatched_orders.length}
              </span>
            </div>
          </div>

          {(result.reprint_order_nos?.length ?? 0) > 0 && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded p-3">
              <p className="font-medium mb-2">
                ⚠ 発送済みの注文が実績 CSV に含まれています ({result.reprint_order_nos!.length} 件)
                — エアロジで引き当て解除 → 再引き当てされた可能性があります。
              </p>
              <ul className="text-xs space-y-1 font-mono mb-2">
                {result.reprint_order_nos!.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
              <p className="text-xs">
                これらの注文は<strong>次回の「印刷実行」で自動的に PDF に含まれます</strong>
                (出荷ラベルは発行済みのもの、ピッキングラベルは最新ロケーション)。
              </p>
            </div>
          )}

          {result.unmatched_orders.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded p-3">
              <p className="font-medium mb-2">DB に見つからなかった注文番号:</p>
              <ul className="text-xs space-y-1 font-mono">
                {result.unmatched_orders.slice(0, 20).map((n) => (
                  <li key={n}>{n}</li>
                ))}
                {result.unmatched_orders.length > 20 && (
                  <li className="text-amber-600">…他 {result.unmatched_orders.length - 20} 件</li>
                )}
              </ul>
            </div>
          )}

          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
              <p className="font-medium mb-2">エラー:</p>
              <ul className="text-xs space-y-1">
                {result.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
